// views/Dashboard/Bonuses/components/BonusDetailModal/BonusDetailModal.tsx
import { useState, useEffect } from 'react';
import Modal from '../../../../../components/common/Modal/Modal';
import Button from '../../../../../components/common/Button/Button';
import CountdownTimer from '../CountdownTimer/CountdownTimer';
import Loading from '../../../../../components/common/Loading/Loading';
import EarningsModal from '../EarningModal/EarningModal';
import { BonusType, Risk } from '../../../../../Entities';
import { notify } from '../../../../../components/common/Toast/toastBridge';
import styles from './BonusDetailModal.module.css';

type StepStatus = 'completed' | 'active' | 'available' | 'locked';

interface BonusDetailModalProps {
  bonus: any;
  onClose: () => void;
  updateStepIdx: (bonusId: number, newStepIdx: number) => Promise<void>;
  user: any;
  setUser: (user: any) => void;
  onBonusFilled: () => void;
}

const BonusDetailModal = ({ bonus, onClose, updateStepIdx, user, setUser, onBonusFilled }: BonusDetailModalProps) => {
  const [currentStep, setCurrentStep] = useState<number>(
    bonus.step_idx !== bonus.step_titles.length ? bonus.step_idx : bonus.step_idx - 1,
  );
  const [completedSteps, setCompletedSteps] = useState<number[]>([...Array(bonus.step_idx)].map((_, i) => i));
  const [processing, setProcessing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showEarningsModal, setShowEarningsModal] = useState(false);

  useEffect(() => {
    const getSteps = async () => {
      try {
        await bonus.getSteps();
        if (bonus.step_idx === 0 && bonus.bonus_type_name !== 'bank' && bonus.start_balance === null) {
          setShowEarningsModal(true);
        }
        setLoading(false);
      } catch (error) {
        console.error(error);
        notify.error('Nastala chyba s načtením bonusu');
        onClose();
      }
    };
    getSteps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onUpdateIdx = async (bonusId: number, newStepIdx: number) => {
    try {
      setProcessing(true);
      await updateStepIdx(bonusId, newStepIdx);
    } catch (error) {
      notify.error('Nepodařilo se zaznamenat krok');
      throw error;
    } finally {
      setProcessing(false);
    }
  };

  const getRiskBadgeClass = (risk: string) => {
    const map: Record<string, string> = {
      free: styles.none,
      low: styles.low,
      medium: styles.medium,
      high: styles.high,
      extreme: styles.extreme,
      ultimate: styles.ultimate,
    };
    return map[risk] ?? styles.low;
  };

  const getStepStatus = (stepIndex: number): StepStatus => {
    if (completedSteps.includes(stepIndex)) return 'completed';
    if (stepIndex === currentStep) return 'active';
    if (stepIndex === 0) return 'available';
    if (stepIndex > 0 && completedSteps.includes(stepIndex - 1)) return 'available';
    return 'locked';
  };

  const handleStepClick = (stepIndex: number) => {
    const status = getStepStatus(stepIndex);
    if (status === 'available' || status === 'completed' || status === 'active') {
      setCurrentStep(stepIndex);
    }
  };

  const canCompleteStep = (stepIndex: number) => {
    if (stepIndex === 0) return true;
    return completedSteps.includes(stepIndex - 1);
  };

  const handleStepComplete = async () => {
    try {
      if (!canCompleteStep(currentStep)) return;
      if (!completedSteps.includes(currentStep)) {
        await onUpdateIdx(bonus.id, currentStep + 1);
        setCompletedSteps((prev) => [...prev, currentStep]);
        if (currentStep < bonus.step_list.length - 1) setCurrentStep(currentStep + 1);
      }
    } catch {
      /* handled in onUpdateIdx */
    }
  };

  const handleStepUncomplete = async () => {
    try {
      if (completedSteps.includes(currentStep)) {
        await onUpdateIdx(bonus.id, currentStep);
        setCompletedSteps(completedSteps.filter((idx) => idx < currentStep));
      }
    } catch (e) {
      console.log(e);
    }
  };

  const handleNextStep = () => {
    if (currentStep < bonus.step_list.length - 1) setCurrentStep(currentStep + 1);
  };
  const handlePrevStep = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  const getCompleteButtonText = () => {
    if (completedSteps.includes(currentStep)) return '✅ Dokončeno';
    if (!canCompleteStep(currentStep)) return '🔒 Splňte předchozí kroky';
    return '✓ Označit krok za dokončený';
  };

  if (loading) {
    return (
      <Modal isOpen onClose={onClose} title={`${bonus.name} - ${bonus.company.name}`} size="large">
        <Loading />
      </Modal>
    );
  }

  // Earnings modal shows at both the start and the end of filling the bonus.
  if (showEarningsModal) {
    return (
      <EarningsModal
        user={user}
        setUser={setUser}
        selectedBonus={bonus}
        onBonusFilled={onBonusFilled}
        setShowEarningsModal={setShowEarningsModal}
      />
    );
  }

  const typeInfo = BonusType.get({ name: bonus.bonus_type_name });
  const isAllStepsCompleted = completedSteps.length === bonus.step_list.length;

  return (
    <Modal isOpen onClose={onClose} size="large">
      <div className={styles.bonusDetail}>
        <div className={styles.header}>
          <div className={styles.headerInfo} data-tour="bonus-modal-info">
            <div className={styles.companyRow}>
              <div className={styles.companyText}>
                <h2 className={styles.bonusTitle}>{bonus.name}</h2>
                <p className={styles.companyName}>{bonus.company.name}</p>
              </div>
            </div>

            <div className={styles.typeInfo}>
              <span className={styles.typeLabel} style={{ background: `${typeInfo.color}20`, color: typeInfo.color }}>
                {typeInfo.icon} {typeInfo.label}
              </span>
              {bonus.special_mark === 'VIP' && <span className={styles.vipBadge}>⭐ VIP</span>}
              {bonus.special_mark === 'recommended' && <span className={styles.recommendedBadge}>💡 Doporučený</span>}
              {bonus.special_mark === 'exceptional' && <span className={styles.exceptionalBadge}>💎 Výjimečný</span>}
            </div>

            <div className={styles.statRow}>
              <div className={styles.statPill}>
                <span className={styles.statLabel}>Očekávaný zisk</span>
                <strong className={styles.statProfit}>{Number(bonus.expected_value)} Kč</strong>
              </div>
              <div className={styles.statPill}>
                <span className={styles.statLabel}>Potřebný kapitál</span>
                <strong className={styles.statCapital}>{Number(bonus.needed_capital)} Kč</strong>
              </div>
              <div className={styles.statPill}>
                <span className={styles.statLabel}>Riziko</span>
                <span className={`${styles.riskBadge} ${getRiskBadgeClass(bonus.risk_rate)}`}>
                  {Risk.values.filter((r) => r.rate === bonus.risk_rate)[0].label}
                </span>
              </div>
            </div>
          </div>

          <div className={styles.countdownContainer}>
            <CountdownTimer startDate={bonus.start_timestamp * 1000} endDate={bonus.end_timestamp * 1000} size="normal" />
          </div>
        </div>

        {!!bonus.step_list.length && (
          <div className={styles.stepperContainer}>
            <h3>Postup krok za krokem:</h3>

            <div className={styles.stepNavigation} data-tour="bonus-modal-steps">
              {bonus.step_list.map((step: any, index: number) => {
                const status = getStepStatus(index);
                return (
                  <div
                    key={index}
                    className={`${styles.stepNavItem} ${index === currentStep ? styles.active : ''} ${status === 'completed' ? styles.completed : ''} ${status === 'locked' ? styles.locked : ''}`}
                    onClick={() => handleStepClick(index)}
                  >
                    <div className={styles.stepNavNumber}>
                      {status === 'completed' ? '✓' : status === 'locked' ? '🔒' : index + 1}
                    </div>
                    <span className={styles.stepNavTitle}>{step.title}</span>
                  </div>
                );
              })}
            </div>

            <div className={styles.currentStepContent}>
              <div className={styles.stepNavButtons}>
                <Button variant="ghost" onClick={handlePrevStep} disabled={currentStep === 0}>
                  ← Předchozí
                </Button>
                <span className={styles.stepProgress}>
                  Krok {currentStep + 1} z {bonus.step_list.length}
                </span>
                <Button variant="ghost" onClick={handleNextStep} disabled={currentStep === bonus.step_list.length - 1}>
                  Další →
                </Button>
              </div>
              <br />
              <div className={styles.stepHeader}>
                <span className={styles.stepIcon}>{bonus.step_list[currentStep].icon}</span>
                <h4>{bonus.step_list[currentStep].title}</h4>
              </div>

              <p className={styles.stepDescription}>{bonus.step_list[currentStep].subtitle}</p>

              <div className={styles.stepDetailBox}>
                <h5>📋 Detailní postup:</h5>
                <div className="ql-editor" dangerouslySetInnerHTML={{ __html: bonus.step_list[currentStep].description }} />
              </div>

              <div className={styles.stepActions}>
                <div className={styles.stepCompletionButtons} data-tour="bonus-modal-complete">
                  <Button
                    variant={completedSteps.includes(currentStep) || !canCompleteStep(currentStep) ? 'secondary' : 'primary'}
                    onClick={handleStepComplete}
                    disabled={completedSteps.includes(currentStep) || !canCompleteStep(currentStep) || processing}
                    title={!canCompleteStep(currentStep) ? 'Pro dokončení tohoto kroku musíte nejdříve dokončit předchozí krok' : ''}
                  >
                    {getCompleteButtonText()}
                  </Button>

                  {completedSteps.includes(currentStep) && (
                    <Button variant="ghost" onClick={handleStepUncomplete} disabled={processing}>
                      ↩️ Zrušit dokončení
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {!!bonus.games.length && (
          <div className={styles.gamesSection}>
            <h3>{bonus.bonus_type_name === 'live' ? '🎲 Doporučené živé hry:' : '🎰 Doporučené automaty:'}</h3>
            <p className={styles.gamesSectionDesc}>
              {bonus.bonus_type_name === 'live'
                ? 'Vybrané živé hry pro plnění tohoto bonusu:'
                : 'Vybrané automaty k plnění tohoto bonusu:'}
            </p>
            <div className={styles.gamesGrid}>
              {bonus.games.map((game: any, index: number) => (
                <div key={index} className={styles.gameCard}>
                  <div className={styles.gameImage}>
                    <img src={`https://src.algapia.cz/pictures/games/${game.name}.png`} alt={game.name} className={styles.gameImageReal} />
                  </div>
                  <div className={styles.gameInfo}>
                    <h4>{game.name}</h4>
                    <p className={styles.gameProvider}>{game.game_studio}</p>
                    <span className={styles.gameRtp}>RTP: {game.rtp}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className={styles.actions} data-tour="bonus-modal-links">
          <Button variant="secondary" onClick={() => window.open(bonus.url_bonus_conditions)}>
            📖 Zobrazit podmínky
          </Button>
          <Button variant="ghost" onClick={() => window.open(bonus.url_link)}>
            🌐 Přejít na stránku
          </Button>
        </div>
        <Button variant="primary" onClick={() => setShowEarningsModal(true)} disabled={!isAllStepsCompleted}>
          {isAllStepsCompleted
            ? '✅ Označit jako dokončené'
            : `📋 Dokončete všechny kroky (${completedSteps.length}/${bonus.step_list.length})`}
        </Button>
      </div>
    </Modal>
  );
};

export default BonusDetailModal;
